import { useState } from "react";
import { api } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PageHeader, LoadingState, EmptyState, ErrorState } from "@/components/features/states";
import { JobStatusBadge, PaymentStatusBadge } from "@/components/features/badges";
import { formatPrice, formatRelativeTime } from "@/lib/format";
import { Package, Search } from "lucide-react";
import { useTranslation } from "react-i18next";

export function AdminJobs() {
  const { t } = useTranslation();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin", "jobs"],
    queryFn: () => api.admin.jobs(),
  });
  const jobs = data?.data?.jobs ?? [];

  const filtered = jobs.filter((j: any) => {
    if (statusFilter !== "all" && j.status !== statusFilter) return false;
    if (q) {
      const s = q.toLowerCase();
      return (
        j.code.toLowerCase().includes(s) ||
        j.parcelDescription.toLowerCase().includes(s) ||
        j.pickupAddress.toLowerCase().includes(s) ||
        j.dropoffAddress.toLowerCase().includes(s)
      );
    }
    return true;
  });

  if (isLoading) return <LoadingState />;
  if (error || !data?.ok) return <ErrorState message={data?.error || t("admin.jobs.loadFailed")} onRetry={refetch} />;

  const statuses = ["all", "open", "assigned", "picked_up", "in_transit", "delivered", "completed", "cancelled", "disputed"];

  return (
    <div className="space-y-4">
      <PageHeader title={t("admin.jobs.title")} subtitle={t("admin.jobs.subtitle", { count: jobs.length })} />

      <div className="flex flex-col gap-2 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            className="h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm"
            placeholder={t("admin.jobs.searchPlaceholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      <div className="scroll-area -mx-3 flex gap-2 overflow-x-auto px-3 pb-1">
        {statuses.map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-medium capitalize transition ${
              statusFilter === s ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
            }`}
          >
            {s.replace("_", " ")}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={<Package className="h-8 w-8" />} title={t("admin.jobs.noJobsFound")} />
      ) : (
        <div className="space-y-2">
          {filtered.map((j: any) => (
            <Card key={j.id}>
              <CardContent className="p-3.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs text-muted-foreground">{j.code}</span>
                      <JobStatusBadge status={j.status} />
                    </div>
                    <p className="mt-0.5 line-clamp-1 text-sm font-semibold">{j.parcelDescription}</p>
                    <p className="line-clamp-1 text-xs text-muted-foreground">{j.pickupAddress} → {j.dropoffAddress}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span>{t("admin.jobs.customer")}: <span className="font-medium text-foreground">{j.customer?.name}</span></span>
                      <span>·</span>
                      <span>{t("admin.jobs.courier")}: <span className="font-medium text-foreground">{j.provider?.name || "—"}</span></span>
                      <span>·</span>
                      <span>{formatRelativeTime(j.createdAt)}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-primary">{formatPrice(j.agreedPrice ?? j.customerOffer)}</p>
                    {j.payment && <div className="mt-1"><PaymentStatusBadge status={j.payment.status} /></div>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
